import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash, GearSix, X, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { FileVault } from "@/components/FileVault";
import { DateField, TimeField } from "@/components/DateTimeField";
import { api, formatApiErrorDetail } from "@/lib/api";

const DEFAULT_ISSUES = [
  "Chip Repair",
  "Seam Repair",
  "Re-caulk",
  "Polish",
  "Reseal",
  "Sink Reset",
  "Crack Repair",
  "Other",
];

const IssueManager = ({ open, onClose, issues, onSaved }) => {
  const [list, setList] = useState(issues);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setList(issues), [issues, open]);
  const add = () => {
    const v = draft.trim();
    if (!v || list.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    setList([...list, v]);
    setDraft("");
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/service-issues", { issues: list });
      onSaved(data.issues);
      toast.success("Issue types saved");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-950 sm:max-w-md" data-testid="issue-manager">
        <DialogHeader>
          <DialogTitle className="text-base">Service call issue types</DialogTitle>
        </DialogHeader>
        <div className="space-y-1">
          {list.map((i) => (
            <div key={i} className="flex items-center justify-between rounded-md bg-zinc-900 px-3 py-2 text-sm" data-testid={`issue-row-${i}`}>
              <span>{i}</span>
              {i !== "Other" && (
                <button type="button" data-testid={`issue-remove-${i}`} onClick={() => setList(list.filter((x) => x !== i))}
                  className="text-zinc-500 hover:text-red-400" title="Remove">
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="issue-new-input" value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="New issue type…" className="h-10 border-zinc-800 bg-zinc-900" />
          <Button type="button" data-testid="issue-add-button" onClick={add} variant="outline" className="h-10 border-zinc-700">
            <Plus size={16} weight="bold" />
          </Button>
        </div>
        <Button type="button" data-testid="issue-save-button" onClick={save} disabled={busy}
          className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest hover:bg-indigo-400">
          {busy ? "Saving…" : "Save issue types"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
const EMPTY = {
  customer_id: "none",
  customer_name: "",
  job_id: "none",
  job_code: "",
  address: "",
  issue_type: "Chip Repair",
  priority: "Normal",
  description: "",
  scheduled_date: "",
  window: "All day",
  time: "",
  crew: "none",
  status: "Open",
};

export default function ServiceCallForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [crew, setCrew] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState(DEFAULT_ISSUES);
  const [manageIssues, setManageIssues] = useState(false);

  useEffect(() => {
    api.get("/settings/service-issues").then(({ data }) => setIssues(data.issues || DEFAULT_ISSUES)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/customers"),
      api.get("/jobs"),
      api.get("/crew-list"),
    ]).then(([c, j, u]) => {
      setCustomers(c.data);
      setJobs(j.data);
      setCrew(u.data);
    });
  }, []);

  useEffect(() => {
    if (id) return;
    const jobParam = params.get("job");
    const custParam = params.get("customer");
    if (jobParam) {
      api.get(`/jobs/${jobParam}`).then(({ data }) =>
        setForm((f) => ({
          ...f,
          job_id: data.id,
          job_code: data.code,
          customer_id: data.customer_id || "none",
          customer_name: data.customer_name || "",
          address: data.address || "",
        })),
      );
    } else if (custParam) {
      const account = customers.find((x) => x.id === custParam);
      if (account)
        setForm((f) =>
          f.customer_id === custParam
            ? f
            : {
                ...f,
                customer_id: account.id,
                customer_name: account.name,
                address: f.address || account.address || "",
              },
        );
    }
  }, [id, params, customers]);

  useEffect(() => {
    if (!id) return;
    api.get(`/service-calls/${id}`).then(({ data }) =>
      setForm({
        ...EMPTY,
        ...data,
        customer_id: data.customer_id || "none",
        job_id: data.job_id || "none",
        crew: data.crew || "none",
        scheduled_date: data.scheduled_date || "",
      }),
    );
  }, [id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pickCustomer = (value) => {
    if (value === "none")
      return setForm((f) => ({ ...f, customer_id: "none", customer_name: "" }));
    const c = customers.find((x) => x.id === value);
    setForm((f) => ({
      ...f,
      customer_id: value,
      customer_name: c?.name || "",
      address: f.address || c?.address || "",
    }));
  };

  const pickJob = (value) => {
    if (value === "none")
      return setForm((f) => ({ ...f, job_id: "none", job_code: "" }));
    const j = jobs.find((x) => x.id === value);
    setForm((f) => ({
      ...f,
      job_id: value,
      job_code: j?.code || "",
      customer_id: j?.customer_id || f.customer_id,
      customer_name: j?.customer_name || f.customer_name,
      address: f.address || j?.address || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      ...form,
      customer_id: form.customer_id === "none" ? null : form.customer_id,
      job_id: form.job_id === "none" ? null : form.job_id,
      crew: form.crew === "none" ? "" : form.crew,
      scheduled_date: form.scheduled_date || null,
      status:
        form.status === "Open" && form.scheduled_date
          ? "Scheduled"
          : form.status,
    };
    delete payload.id;
    delete payload.code;
    delete payload.files;
    delete payload.photos;
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.created_by;
    delete payload.updated_by;
    try {
      const { data } = id
        ? await api.put(`/service-calls/${id}`, payload)
        : await api.post("/service-calls", payload);
      toast.success(
        id ? "Service call updated" : `Service call ${data.code} created`,
      );
      navigate("/projects/service");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const remove = async () => {
    await api.delete(`/service-calls/${id}`);
    toast.success("Service call deleted");
    navigate("/projects/service");
  };

  const dropdown = (label, key, options, onChange) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Select value={form[key]} onValueChange={onChange || set(key)}>
        <SelectTrigger
          data-testid={`service-${key}-select`}
          className="h-12 border-zinc-800 bg-zinc-900 text-base"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-zinc-800 bg-zinc-900">
          {options.map(([value, text]) => (
            <SelectItem
              key={value}
              value={value}
              data-testid={`service-${key}-${value}`}
            >
              {text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Edit service call" : "New service call"} />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-32 pt-6">
        <button
          onClick={() => navigate("/projects/service")}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Service calls
        </button>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Service Call" : "New Service Call"}
        </h1>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5"
          data-testid="service-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            {dropdown(
              "Customer",
              "customer_id",
              [
                ["none", "No customer"],
                ...customers.map((c) => [c.id, c.name]),
              ],
              pickCustomer,
            )}
            {dropdown(
              "Related Job",
              "job_id",
              [
                ["none", "No job"],
                ...jobs.map((j) => [j.id, `${j.code} — ${j.project_name}`]),
              ],
              pickJob,
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Job Address
            </Label>
            <Input
              data-testid="service-address-input"
              value={form.address}
              onChange={(e) => set("address")(e.target.value)}
              placeholder="142 Elm St, Springfield"
              className="h-12 border-zinc-800 bg-zinc-900 text-base"
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div className="relative">
              {dropdown(
                "Issue",
                "issue_type",
                [...new Set([...issues, form.issue_type].filter(Boolean))].map((i) => [i, i]),
              )}
              <button
                type="button"
                data-testid="manage-issues-button"
                onClick={() => setManageIssues(true)}
                title="Add or remove issue types"
                className="absolute right-0 top-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-indigo-300 hover:text-indigo-200"
              >
                <GearSix size={12} weight="bold" /> Manage
              </button>
            </div>
            {dropdown("Priority", "priority", [
              ["Urgent", "Urgent"],
              ["Normal", "Normal"],
              ["Low", "Low"],
            ])}
            {dropdown("Status", "status", [
              ["Open", "Open"],
              ["Scheduled", "Scheduled"],
              ["On site", "On site"],
              ["Done", "Done"],
            ])}
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Scheduled Date
              </Label>
              <DateField
                testId="service-date-input"
                value={form.scheduled_date}
                onChange={set("scheduled_date")}
              />
            </div>
            {dropdown("Window", "window", [
              ["All day", "All day"],
              ["AM", "AM"],
              ["PM", "PM"],
            ])}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Time
              </Label>
              <TimeField
                testId="service-time-input"
                value={form.time || ""}
                onChange={set("time")}
              />
            </div>
            {dropdown("Crew", "crew", [
              ["none", "Unassigned"],
              ...crew.map((u) => [u.name || u.email, u.name || u.email]),
            ])}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Description
            </Label>
            <Textarea
              data-testid="service-description-input"
              value={form.description}
              onChange={(e) => set("description")(e.target.value)}
              rows={4}
              placeholder="Chip at sink cutout, customer sent photo, bring color-matched epoxy…"
              className="border-zinc-800 bg-zinc-900 text-base"
            />
          </div>

          {id && (
            <FileVault
              entity="service-calls"
              entityId={id}
              title="Service Photos & Docs"
            />
          )}

          {error && (
            <p
              data-testid="service-form-error"
              className="text-sm font-medium text-red-400"
            >
              {error}
            </p>
          )}

          <div className="no-print fixed bottom-0 left-0 right-0 flex gap-3 border-t border-zinc-800 bg-zinc-950 p-3 sm:static sm:border-0 sm:bg-transparent sm:p-0">
            <Button
              data-testid="save-service-button"
              type="submit"
              disabled={busy}
              className="h-12 flex-1 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 sm:flex-none sm:px-10"
            >
              {busy ? "Saving…" : id ? "Save Changes" : "Create Service Call"}
            </Button>
            {id && (
              <Button
                data-testid="delete-service-button"
                type="button"
                variant="outline"
                onClick={remove}
                className="h-12 border-red-900/60 bg-transparent font-semibold uppercase tracking-wider text-red-400 hover:bg-red-950/40"
              >
                <Trash size={18} weight="bold" />
              </Button>
            )}
          </div>
        </form>
        <IssueManager
          open={manageIssues}
          onClose={() => setManageIssues(false)}
          issues={issues}
          onSaved={(next) => {
            setIssues(next);
            if (!next.includes(form.issue_type)) set("issue_type")(next[0]);
          }}
        />
      </main>
    </div>
  );
}
